import { ApiClient } from '@api/api-configuration';
import { captureError } from '@app/lib/Sentry';
import { Tag, message } from 'antd';
import { FiAlertCircle, FiDownload } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { DataExportCompletedNotificationType, DataExportFailedNotificationType } from '../../types/notifications';
import { notificationSentAtFormattedDate } from '../../utils';

type DataExportNotificationCardProps = {
  notificationDetails: DataExportCompletedNotificationType | DataExportFailedNotificationType;
  onClick: () => void;
};

const DataExportNotificationCard = ({ notificationDetails, onClick }: DataExportNotificationCardProps) => {
  const { t } = useTranslation();
  const meta = notificationDetails.metadata?.[0];
  const isSuccess = meta?.success !== false;
  const filePath = meta?.filePath;

  const handleDownload = async (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!filePath) {
      message.error(t('notifications-download-no-file'));
      return;
    }

    // Determine which API to use based on the file path or notification type
    const isVisitExport =
      filePath.includes('visits_export') ||
      filePath.includes('/visits/') ||
      notificationDetails.title.toLowerCase().includes('visit');

    try {
      // Call the download API - the response should be the CSV data
      if (isVisitExport) {
        const response = await ApiClient.visitsApi.visits.downloadVisitExport({
          file_path: filePath,
        });
        // response.data is already the CSV string
        const blob = new Blob([response.data], { type: 'text/csv' });
        triggerDownload(blob, filePath);
      } else {
        const response = await ApiClient.serviceRequestsApi.serviceRequests.downloadServiceRequestExport({
          file_path: filePath,
        });
        // response.data is already the CSV string
        const blob = new Blob([response.data], { type: 'text/csv' });
        triggerDownload(blob, filePath);
      }

      message.success(t('notifications-download-success'));

      // Mark notification as read after successful download
      onClick();
    } catch (error) {
      captureError(error as Error, {
        context: 'Export file download',
        filePath,
        isVisitExport,
      });
      message.error(t('notifications-download-error'));
    }
  };

  const triggerDownload = (blob: Blob, path: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = path.split('/').pop() || 'export.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div
      onClick={isSuccess && filePath ? handleDownload : onClick}
      className={`group relative flex gap-4 rounded-xl border p-6 transition-all hover:shadow-lg cursor-pointer ${
        notificationDetails.isRead ? 'border-gray-200 bg-white' : 'border-blue-200 bg-blue-50'
      }`}
    >
      {/* Icon */}
      <div
        className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full ${
          isSuccess ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
        }`}
      >
        {isSuccess ? <FiDownload size={24} /> : <FiAlertCircle size={24} />}
      </div>

      {/* Content */}
      <div className="min-w-0 flex-1">
        {/* Header */}
        <div className="mb-2 flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <h3 className="mb-1 text-lg font-semibold text-gray-900">{notificationDetails.title}</h3>
            <p className="text-sm text-gray-600">{notificationDetails.message}</p>
          </div>
          <Tag color={isSuccess ? 'success' : 'error'}>
            {isSuccess ? t('notifications-tag-completed') : t('notifications-tag-failed')}
          </Tag>
        </div>

        {/* Download Button for success */}
        {isSuccess && filePath && (
          <div className="mt-3">
            <button
              onClick={handleDownload}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 transition-colors"
            >
              <FiDownload size={16} />
              {t('notifications-download-export-file')}
            </button>
          </div>
        )}

        {/* Footer */}
        <div className="mt-4 flex items-center justify-between text-xs text-gray-500">
          <span>{notificationSentAtFormattedDate(notificationDetails.createdAt)}</span>
          {!notificationDetails.isRead && (
            <span className="rounded-full bg-blue-500 px-2 py-1 text-xs font-medium text-white">
              {t('notifications-new-badge')}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default DataExportNotificationCard;
